import React, { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { useTransition, animated } from '@react-spring/web'
import icons from '../KidsHome/icons'

const srcJesusChrist =
  'https://common-discovery-service-pgp.familysearchcdn.org/connect/temple/v1/jesus-christ-heinrich-hofmann.png'

const imageStyles = {
  borderRadius: '50%',
  width: '196px',
  maxWidth: '196px',
  height: '196px',
  maxHeight: '196px',
  objectFit: 'cover',
}

function CompositeTempleImage({ templeImage, centered }) {
  const [t] = useTranslation()
  const vectorAlt = t('temple.hero.vector-temple', 'Vector illustration of a temple')
  const photoAlt = t('temple.hero.assigned-temple', 'Photo of your assigned temple')
  const christAlt = t('temple.hero.christ-portrait', 'Portrait of Jesus Christ')

  const fallbackImg = {
    testId: 'temple-fallback',
    src: icons?.placeholderTemple,
    alt: vectorAlt,
  }

  const templeImg = {
    testId: 'temple-current',
    src: templeImage,
    alt: photoAlt,
  }

  const [showFallback, setShowFallback] = useState(true) // default to svg temple illustration

  // if assigned temple image becomes available, crossfade to it & unmount svg image after transition
  const transitions = useTransition(!!templeImage, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 1000 }, // slower fade to leave svg noticeable before transition
    onRest: (_result, _ctrl, item) => !!item && setShowFallback(false),
  })

  // TODO: cache temple image temporarily so we don't have to fetch it & fade in every refresh/temple tab click

  return (
    <div style={{ display: 'flex', justifyContent: centered ? 'center' : 'end', position: 'relative' }}>
      <img
        loading="eager"
        alt={christAlt}
        src={srcJesusChrist}
        style={{ ...imageStyles, zIndex: 1, marginInlineEnd: '-24px' }}
      />
      <div style={{ position: 'relative', ...imageStyles }}>
        {showFallback && (
          <img
            data-testid={fallbackImg?.testId}
            loading="eager"
            src={fallbackImg?.src}
            alt={fallbackImg?.alt}
            style={{ ...imageStyles, position: 'absolute' }}
          />
        )}
        {transitions((style, item) =>
          item ? (
            <animated.img
              data-testid={templeImg?.testId}
              loading="eager"
              src={templeImg?.src}
              alt={templeImg?.alt}
              style={{ ...style, ...imageStyles, position: 'absolute' }}
            />
          ) : null
        )}
      </div>
    </div>
  )
}

CompositeTempleImage.propTypes = {
  templeImage: PropTypes.string,
  centered: PropTypes.bool,
}

export default memo(CompositeTempleImage)
